import React, { useState, useEffect } from 'react';
import { useAuthorization } from 'contexts/AuthContextProvider.jsx';
import { useTranslation, Trans } from 'react-i18next';
import workflowProIcon from '../assets/img/login/WorkFlow Pro icon.png';
import '../css/views/Login.css';
import { CardBody, FormGroup, Form, Row, Col } from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Typography } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from "@mui/material/IconButton";
import SendIcon from '@mui/icons-material/Send';

/* eslint-disable react-hooks/exhaustive-deps */
const ActivateAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sendVerificationLink, sendVerificationToken, loginViaBackendTokens } = useAuthorization();
  const [isCooldown, setIsCooldown] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(t("verify.sending"));
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');

  const location = useLocation(); // To get the current URL

  useEffect(() => {
    document.title = t('titles.activate_account');
  }, []);

  // Extract the email from the query parameters in the URL
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const register_token = queryParams.get('token');
    const registeredEmail = queryParams.get('registered_email');

    if (register_token) {
      setToken(register_token);
    } else {
      setToken(null);
    }
    if (registeredEmail) {
      setEmail(registeredEmail);
    }
  }, [location.search]);

  const handleClick = () => {
    window.location.href = '/';
  };


  useEffect(() => {
    if (email && token === null) {
      handleSendVerificationLink();
    }
  }, [email, token]);

  useEffect(() => {
    if (email && (token !== null || token !== '')) {
      handleSendVerificationToken(email, token);
    }
  }, [email, token]);


  const handleSendVerificationLink = async () => {
    try {
      setError('');
      if (email) {
        await sendVerificationLink(email);
        setSuccess(t('verify.link_sent'));
        setIsCooldown(true);
        setCountdown(60);
      } else {
        setError(t('verify.email_missing'));
      }
    } catch (error) {
      setSuccess(t('verify.link_sent'));
    }
  };

  const handleSendVerificationToken = async () => {
    try {
      setError('');
      if (email && token) {
        setSuccess(t('verify.verifying'))
        const response = await sendVerificationToken(email, token);
        if (response.detail === "Email successfully verified." && response.access && response.refresh) {
          setSuccess(t('verify.verified'));

          await loginViaBackendTokens(response.access, response.refresh);

          //Wait a moment to show success msg, then navigate to it, just to allow the user to read it
          setTimeout(() => {
            navigate('/workflow/index');
          }, 500);
        }
      }
    } catch (error) {
      if (error?.response?.data?.detail)
        setError(error?.response?.data?.detail);
      setSuccess('');
    }
  };

  useEffect(() => {
    let timer;
    if (isCooldown) {
      // Set up a timer for the countdown
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }

    // Stop the countdown and re-enable the button after 60 seconds
    if (countdown === 0) {
      setIsCooldown(false);
      clearInterval(timer);
    }

    return () => clearInterval(timer);  // Cleanup the timer on component unmount
  }, [isCooldown, countdown]);

  return (
    <>
      <div className="background">
        <div className="login-container">
          <div className="login-card">
            <CardBody>
              <div className="login-header">
                <div
                  onClick={handleClick}
                  className="login-back"
                  aria-label="back"
                >
                  <IconButton onClick={handleClick} aria-label="back"
                    className="login-back-icon">
                    <ArrowBackIcon />
                  </IconButton>
                  <span className="login-back-icon-margin">{t("verify.back")} </span>
                </div>
              </div>
              <div className="form-container">
                <div className="login-logo">
                  <img src={workflowProIcon} alt="Workflow Pro" />
                </div>

                {token === null ? (
                  <>
                    <Typography>
                      {t('verify.you_need_to_activate_account_in_order_to_use')}
                    </Typography>
                    <Typography>
                      <Trans i18nKey="verify.time_to_activate" />
                    </Typography>

                    {email && (
                      <Typography>
                        {t('verify.verification_email_for')} {email}
                      </Typography>
                    )}

                    <Form role="form" className="login-form">
                      <FormGroup className="mb-1">
                        <Row className="login-error">
                          <Col className="text-center">
                            {error && <small className="error-message">{error}</small>}
                            {success && <small className="success-message">{success}</small>}
                          </Col>
                        </Row>
                      </FormGroup>

                      <div className="text-center">
                        <Button
                          onClick={handleSendVerificationLink}
                          className="custom-button"
                          disabled={isCooldown}  // Disable button during cooldown
                        >
                          {isCooldown ? `${t("verify.wait")} (${countdown}s)` : t("verify.send_link")}
                          <SendIcon sx={{ ml: 2 }} />
                        </Button>
                      </div>

                      <div className="text-center footer-links">
                        <small className="footer-link">
                          <a href="/#help">{t("verify.contact_support")}</a>
                        </small>
                      </div>
                    </Form>
                  </>
                ) : (
                  <>


                    {email && (
                      <Typography>
                        {email}
                      </Typography>
                    )}

                    <Form role="form" className="login-form">
                      <FormGroup className="mb-1">
                        <Row className="login-error">
                          <Col className="text-center">
                            {error && <small className="error-message">{error}</small>}
                            {success && <small className="success-message">{success}</small>}
                          </Col>
                        </Row>
                      </FormGroup>
                      <div className="text-center footer-links">
                        <small className="footer-link">
                          <a href="/#help">{t("verify.contact_support")}</a>
                        </small>
                      </div>
                    </Form>
                  </>
                )}
              </div>
            </CardBody>
          </div>
        </div>
      </div>
    </>
  );
};

export default ActivateAccount;
